import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DiarySum from '../../components/diary/DiarySum';
import ActionStatusText from '../../components/ui/ActionStatusText';
import ErrorMessage from '../../components/ui/ErrorMessage';
import { useDiarySection } from '../../context/DiarySectionsContext';
import {
    useModalEntryType,
    useModalNavigation,
    useModalPayload,
    useModalSearch
} from '../../context/ModalControllerContext';
import { useEditableMeal, useModalEntry } from '../../context/ModalEntryContext';
import { calculateNutrientsByAmount, getNowHHmm, hasNewMealItems } from '../../helpers';
import useDeleteMeal from '../../hooks/data/meals/useDeleteMeal';
import useMealActions from '../../hooks/modal/useMealActions';
import useIsOverflowing from '../../hooks/ui/useIsOverflowing';
import useScrollShadow from '../../hooks/ui/useScrollShadow';
import MealItemEditor from '../components/MealItemEditor';
import ModalHeader from '../components/ModalHeader';
import { getMealTypeTitle } from '../../helpers/domain';

const ModalMealEditor = ({ currentDay, actionStatusText, isUpdatingFavoriteMeal = false }) => {
    const { selectedEntry, selectedEntryId } = useModalEntry();
    const { entryType } = useModalEntryType();

    const { editableMeal, setEditableMeal } = useEditableMeal();

    const { close, closeWithDelay, setView, goBack, canGoBack } = useModalNavigation();

    const { closeSection } = useDiarySection();
    const payload = useModalPayload();

    const [hasMounted, setHasMounted] = useState(false);

    const { onClearSearchInput } = useModalSearch();

    // ACTIONS

    const { mutateMeal, isLoading, isSuccess, isError, error } = useMealActions({
        currentDay,
        entryType,
        closeWithDelay
    });

    const {
        deleteMeal,
        isDeleting,
        isSuccess: isDeleteSuccess,
        isError: isDeleteError,
        error: deleteError
    } = useDeleteMeal(currentDay);

    const timeRef = useRef(null);
    const frozenMealRef = useRef(null);

    const [itemIdToDelete, setItemIdToDelete] = useState(null);

    const { ref: contentRef, isOverflowing } = useIsOverflowing([editableMeal.items.length]);

    // HANDLERS

    const handleItemChange = useCallback(
        (itemId, { value, unit }) => {
            const amount = Number.isFinite(Number(value)) ? Number(value) : 0;

            setEditableMeal((prev) => ({
                ...prev,
                items: prev.items.map((item) => {
                    if (item.id !== itemId) return item;

                    const recalculated = calculateNutrientsByAmount(item.base, amount, unit);

                    return {
                        ...item,
                        amount,
                        unit,
                        ...recalculated
                    };
                })
            }));
        },
        [setEditableMeal]
    );

    const toggleTimePicker = (e) => {
        if (e.target.type === 'time') timeRef.current?.showPicker();
    };

    const deleteItemById = (itemId) => {
        setEditableMeal((prev) => ({
            ...prev,
            items: prev.items.filter((item) => item.id !== itemId)
        }));
    };

    const handleDeleteItem = (itemId) => {
        const isLastItem = editableMeal.items.length === 1;

        if (isLastItem && !selectedEntryId) {
            deleteItemById(itemId);
            goBack();
            return;
        }

        if (isLastItem && selectedEntryId) {
            setItemIdToDelete(itemId);
            return;
        }

        deleteItemById(itemId);
    };

    const confirmDeleteItem = async () => {
        if (!selectedEntryId) return;

        if (payload?.slotIndex) closeSection(payload?.slotIndex);

        await deleteMeal(selectedEntryId);

        closeWithDelay();
    };

    // EFFECTS

    useEffect(() => {
        // Only when creating a new meal
        if (selectedEntryId) return;

        if (!editableMeal.time || editableMeal.time === '00:00') {
            setEditableMeal((prev) => ({
                ...prev,
                time: getNowHHmm()
            }));
        }
    }, [entryType, selectedEntryId, setEditableMeal]);

    useEffect(() => () => (frozenMealRef.current = null), []);
    useEffect(() => {
        setHasMounted(true);
    }, []);

    const showShadow = useScrollShadow({
        contentRef,
        isOverflowing,
        deps: [editableMeal.items.length]
    });

    // DERIVED

    const isAmountInvalid = useMemo(
        () => editableMeal.items.some((item) => !item.amount || item.amount <= 0),
        [editableMeal.items]
    );

    const mealTitle = getMealTypeTitle(entryType);

    const hasEntry = selectedEntryId != null;
    const hasNewItems = hasNewMealItems(editableMeal, selectedEntry);

    // CREATE = store (new OR fork)
    // UPDATE = update (same items, value changes only)
    const shouldCreate = !hasEntry || hasNewItems;
    const isNewMeal = !hasEntry;

    const isLocked = isLoading || isSuccess || isUpdatingFavoriteMeal;

    const mealToRender = isLocked && frozenMealRef.current ? frozenMealRef.current : editableMeal;

    const sortedItems = useMemo(() => {
        return [...mealToRender.items].sort((a, b) => {
            if (a._isNew && b._isNew) {
                return (b._addedAt ?? 0) - (a._addedAt ?? 0);
            }

            if (a._isNew) return -1;
            if (b._isNew) return 1;

            return 0;
        });
    }, [mealToRender.items]);

    const isTemplateMeal = editableMeal.isFavorite || editableMeal.isRecipe;

    return (
        <>
            <ModalHeader
                title="Redigera måltid"
                onClose={close}
                onGoBack={goBack}
                disableButtons={isLocked}
                showBack={canGoBack}
            />

            <div className={`modal-body edit-meal ${isOverflowing ? 'has-scroll' : ''}`}>
                <motion.div className={`meal-title-wrapper ${isTemplateMeal ? 'has-icon' : ''}`}>
                    <motion.div
                        className="meal-title-icon"
                        animate={
                            hasMounted
                                ? {
                                    width: isTemplateMeal ? 18 : 0,
                                    opacity: isTemplateMeal ? 1 : 0
                                }
                                : false
                        }
                        transition={{
                            width: { duration: 0.2, ease: 'easeOut' },
                            opacity: { duration: 0.15, delay: 0.05 }
                        }}
                        style={{ overflow: 'hidden' }}
                    >
                        {editableMeal.isFavorite && (
                            <img
                                id="favorite-icon"
                                src="/assets/images/diary-icons/heart-gray-secondary-lg.svg"
                                alt="Favoritmåltid"
                            />
                        )}

                        {editableMeal.isRecipe && (
                            <img
                                id="recipe-icon"
                                src="/assets/images/diary-icons/book-open.svg"
                                alt="Receptmåltid"
                            />
                        )}
                    </motion.div>

                    <h4>
                        {mealTitle} {selectedEntry?.time ? selectedEntry.time : null}
                    </h4>
                </motion.div>

                <div className="edit-meal-content" ref={contentRef}>
                    {!isError ? (
                        sortedItems.map((item) => {
                            console.log(item)
                            return (
                                <div key={item.id} className="edit-meal-item-wrapper">
                                    <div className="item-info">
                                        <div className="item-title-amount">
                                            {!isNewMeal && item._isNew && <span className="new-label">NY</span>}
                                            <span className="bold-title">
                                                {`${item.amount.toLocaleString('sv-SE', {
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: 2
                                                })} ${item.unit?.name ? `${item.unit.name}${item.unit.text?.length > 0 ? ` (${item.unit.text})` : ""}` : item.unit?.text ?? "gram"}`}
                                            </span>{' '}
                                            {item.name}{' '}
                                        </div>

                                        <span className="item-kcal">{Math.round(item.kcal) ?? 0} kcal</span>
                                    </div>

                                    <MealItemEditor
                                        initialValue={item.amount}
                                        initialUnit={item.unit}
                                        options={item.units}
                                        onChangeValue={(data) => handleItemChange(item.id, data)}
                                        onDelete={() => handleDeleteItem(item.id)}
                                        isDisabled={isLocked}
                                        isCustom={!item.food_item_id}
                                    />
                                </div>
                            )
                        })
                    ) : (
                        <ErrorMessage message={error?.message} />
                    )}
                </div>
            </div>

            <div className={`modal-footer edit-meal ${showShadow ? 'has-shadow' : ''}`}>
                <DiarySum items={mealToRender.items} />

                <div className="meal-editor-input">
                    <span className="inline-label">Namn:</span>
                    <input
                        type="text"
                        className="modal-input name-input"
                        value={mealToRender.name || ''}
                        onInput={(e) =>
                            setEditableMeal((prev) => ({
                                ...prev,
                                name: e.target.value
                            }))
                        }
                        disabled={isLocked}
                    />
                </div>

                <div className="meal-editor-input" onClick={toggleTimePicker}>
                    <span className="inline-label">Klockslag:</span>
                    <input
                        type="time"
                        ref={timeRef}
                        className="modal-input time-input"
                        value={mealToRender.time || ''}
                        onInput={(e) =>
                            setEditableMeal((prev) => ({
                                ...prev,
                                time: e.target.value
                            }))
                        }
                        disabled={isLocked}
                    />
                </div>

                <button
                    type="button"
                    className="btn-green bold-title"
                    disabled={isAmountInvalid || isLocked}
                    onClick={() => {
                        onClearSearchInput();
                        setView('default');
                    }}
                >
                    Lägg till fler livsmedel
                </button>

                <button
                    className="btn-green bold-title"
                    disabled={isAmountInvalid || isLocked}
                    onClick={() => {
                        if (!frozenMealRef.current) frozenMealRef.current = editableMeal;

                        mutateMeal({
                            mode: shouldCreate ? 'create' : 'update',
                            mealId: selectedEntryId,
                            mealData: editableMeal
                        });
                    }}
                >
                    {actionStatusText || (
                        <ActionStatusText
                            defaultLabel={shouldCreate ? 'Spara till matdagbok' : 'Spara ändringar'}
                            successLabel={shouldCreate ? 'Måltid sparad!' : 'Ändringar har sparats!'}
                            isProcessing={isLoading}
                            isCompleted={isSuccess}
                        />
                    )}
                </button>
            </div>

            {itemIdToDelete !== null && (
                <div className="confirm-overlay">
                    <div className="confirm-card delete-confirm">
                        <ModalHeader
                            showBack={false}
                            onClose={() => setItemIdToDelete(null)}
                            title="Radera måltid"
                            disableButtons={isDeleting || isDeleteSuccess}
                        />

                        <div className="modal-body delete-confirm">
                            {!isDeleteError ? (
                                <div className="content-text">
                                    <p>Är du säker på att du vill radera måltiden?</p>
                                </div>
                            ) : (
                                <ErrorMessage
                                    message={deleteError?.message || 'Något gick fel vid borttagningen.'}
                                />
                            )}
                        </div>

                        <div className="modal-footer delete-confirm">
                            <button
                                className="btn-red"
                                onClick={confirmDeleteItem}
                                disabled={isDeleting || isDeleteSuccess}
                            >
                                <ActionStatusText
                                    processingLabel="Raderar"
                                    successLabel="Måltiden raderades"
                                    defaultLabel="Radera"
                                    isProcessing={isDeleting}
                                    isCompleted={isDeleteSuccess}
                                />
                            </button>
                            <button
                                className="marked-border-btn"
                                onClick={() => setItemIdToDelete(null)}
                                disabled={isDeleting || isDeleteSuccess}
                            >
                                Avbryt
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ModalMealEditor;
